import React from "react";
import {Section} from "../../../../../elements/section/Section";
import {ValidatedInputField} from "../../../../../elements/validator/component/ValidatedInputField";
import {ValidatedSelectField} from "../../../../../elements/validator/component/ValidatedSelectField";
import {CustomSwitchFieldWithTitle} from "../../../../../elements/customSwitchFieldWithTitle/CustomSwitchFieldWithTitle";
import {LocationQuoteeIdsTable} from "./financeComponents/LocationQuoteeIdsTable";
import {useAppDispatch, useAppSelector} from "../../../../../../app/hooks";
import {
    selectFinanceProvider,
    selectFinanceIddUrl,
    selectFinanceInterestRate,
    selectIsPcpEnabled,
    selectIvendiApiKey,
    selectIvendiClientId,
    selectIvendiClientSecret,
    selectIvendiProviderConfig,
    selectPcpAPR,
    selectPcpDealerContribution,
    selectPcpResidualPercentage,
    toolSettingsSelector,
    updateSettings
} from "../toolSettingsSlice";
import {BotType, FinanceProvider, UpdateBotToolSettingsRequest} from "../../../../../../api/generated";
import {DisabledField, isFieldDisabled} from "../../../assistantPreset";
import {
    DefaultValidValidator,
    FloatNumberValidator,
    NotEmptyStringValidator,
    PercentageValidator,
    UrlValidator
} from "../../../../../elements/validator/Validator";
import {ValidationResult} from "../../../../../elements/validator/component/ValidationComponent";
import {SelectOption} from "../../../../../elements/select/SelectDropdown";
import styles from "../ToolSettingsForm.module.css";
import {getFinanceProviderTitle} from "../../../../../../utils/enumValueTitleUtil";

interface FinanceSettingsProps {
    botType: BotType;
    updateValidationStatus: (status: ValidationResult<UpdateBotToolSettingsRequest>) => void;
    getMessageWrapper: (f: string) => string;
}

const financeProviderOptions: SelectOption<FinanceProvider>[] = [
    FinanceProvider.Illustration,
    FinanceProvider.Ivendi,
    FinanceProvider.KeyloopFinanceService
].map(i => {
    return {
        label: getFinanceProviderTitle(i),
        value: i
    }
});

export const FinanceSettings: React.FC<FinanceSettingsProps> = ({
    botType,
    updateValidationStatus,
    getMessageWrapper
}) => {
    const dispatch = useAppDispatch();
    const financeProvider = useAppSelector(selectFinanceProvider);
    const ivendiConfig = useAppSelector(selectIvendiProviderConfig);
    const toolSettings = useAppSelector(toolSettingsSelector);
    const isPcpEnabled = useAppSelector(selectIsPcpEnabled);

    const isIddUrlDisabled = isFieldDisabled(DisabledField.IDD_URL, botType);
    const isInterestRateDisabled = isFieldDisabled(DisabledField.INTEREST_RATE, botType)
        || financeProvider !== FinanceProvider.Illustration;
    const isProviderDisabled = isFieldDisabled(DisabledField.FINANCE_PROVIDER, botType);
    const isPriceDiscountDisabled = isFieldDisabled(DisabledField.VEHICLE_PRICE_DISCOUNT, botType);
    const isPcpSwitchDisabled = isPriceDiscountDisabled;
    const isPcpIllustrationDisabled = financeProvider !== FinanceProvider.Illustration || !isPcpEnabled || isPriceDiscountDisabled;
    const isIvendiSelected = financeProvider === FinanceProvider.Ivendi;
    const isIllustrationSelected = financeProvider === FinanceProvider.Illustration;

    const handleFinanceMethodChange = (method: FinanceProvider) => {
        if (method === FinanceProvider.Ivendi && !ivendiConfig) {
            const originalIvendiConfig = toolSettings?.ivendiProviderConfig;
            dispatch(updateSettings({
                financeProvider: method,
                ivendiProviderConfig: originalIvendiConfig ? {
                    id: originalIvendiConfig.id,
                    apiKey: originalIvendiConfig.apiKey,
                    clientId: originalIvendiConfig.clientId,
                    clientSecret: originalIvendiConfig.clientSecret,
                    locationQuoteeIds: originalIvendiConfig.locationQuoteeIds || []
                } : {
                    id: 0,
                    apiKey: undefined,
                    clientId: undefined,
                    clientSecret: undefined,
                    locationQuoteeIds: []
                }
            }));
            return;
        }

        dispatch(updateSettings({financeProvider: method}));
    };

    const handleIvendiConfigChange = (field: string, value: string | undefined) => {
        const originalIvendiConfig = toolSettings?.ivendiProviderConfig;
        const configId = ivendiConfig?.id || originalIvendiConfig?.id || 0;
        const currentConfig = ivendiConfig || {
            id: configId,
            apiKey: originalIvendiConfig?.apiKey,
            clientId: originalIvendiConfig?.clientId,
            clientSecret: originalIvendiConfig?.clientSecret,
            locationQuoteeIds: originalIvendiConfig?.locationQuoteeIds || []
        };

        dispatch(updateSettings({
            ivendiProviderConfig: {...currentConfig, id: configId, [field]: value}
        }));
    };

    const locationQuoteeIds = toolSettings?.ivendiProviderConfig?.locationQuoteeIds || [];

    return (
        <Section title="Finance">
            <div className={styles.fieldsLineWithMargin}>
                {!isIddUrlDisabled && (
                    <ValidatedInputField<string, UpdateBotToolSettingsRequest>
                        required={true}
                        onChange={(v) => dispatch(updateSettings({financeIddUrl: v}))}
                        updateValidationStatus={updateValidationStatus}
                        fieldName={'financeIddUrl'}
                        selector={selectFinanceIddUrl}
                        validator={UrlValidator}
                        disabled={false}
                        label={'IDD URL'}
                        placeholder={"https://example.com"}
                        getMessage={getMessageWrapper}
                    />
                )}
                <ValidatedSelectField<FinanceProvider, UpdateBotToolSettingsRequest>
                    required={false}
                    onChange={handleFinanceMethodChange}
                    updateValidationStatus={updateValidationStatus}
                    fieldName={'financeProvider'}
                    selector={selectFinanceProvider}
                    validator={DefaultValidValidator}
                    disabled={isProviderDisabled}
                    label={'Finance Provider'}
                    options={financeProviderOptions}
                    getMessage={getMessageWrapper}
                    selectFirstOptionByDefault={false}
                />
            </div>

            {isIllustrationSelected && (
                <div className={styles.fieldsLineWithMargin}>
                    <ValidatedInputField<number, UpdateBotToolSettingsRequest>
                        required={!isInterestRateDisabled}
                        onChange={(v) => dispatch(updateSettings({financeInterestRate: v}))}
                        updateValidationStatus={updateValidationStatus}
                        fieldName={'financeInterestRate'}
                        selector={selectFinanceInterestRate}
                        validator={PercentageValidator}
                        disabled={isInterestRateDisabled}
                        label={'Interest Rate'}
                        placeholder={"e.g., 11.9"}
                        getMessage={getMessageWrapper}
                    />
                </div>
            )}

            <div className={styles.fieldsLineWithMargin}>
                <CustomSwitchFieldWithTitle
                    textOnTrue={"Enabled"}
                    textOnFalse={"Disabled"}
                    title={"PCP Enabled"}
                    description={""}
                    valueSelector={(state) => state.toolSettings.updateRequest.isPcpEnabled}
                    toggleValue={(v) => dispatch(updateSettings({isPcpEnabled: v}))}
                    disabled={isPcpSwitchDisabled}
                />
            </div>

            {isIllustrationSelected && (
                <div className={styles.fieldsLineWithMargin}>
                    <ValidatedInputField<number, UpdateBotToolSettingsRequest>
                        required={false}
                        onChange={(v) => dispatch(updateSettings({pcpAPR: v}))}
                        updateValidationStatus={updateValidationStatus}
                        fieldName={'pcpAPR'}
                        selector={selectPcpAPR}
                        validator={PercentageValidator}
                        disabled={isPcpIllustrationDisabled}
                        label={'PCP APR'}
                        placeholder={"e.g., 11.9"}
                        getMessage={getMessageWrapper}
                    />
                    <ValidatedInputField<number, UpdateBotToolSettingsRequest>
                        required={false}
                        onChange={(v) => dispatch(updateSettings({pcpResidualPercentage: v}))}
                        updateValidationStatus={updateValidationStatus}
                        fieldName={'pcpResidualPercentage'}
                        selector={selectPcpResidualPercentage}
                        validator={PercentageValidator}
                        disabled={isPcpIllustrationDisabled}
                        label={'PCP Residual Percentage'}
                        placeholder={"e.g., 40"}
                        getMessage={getMessageWrapper}
                    />
                    <ValidatedInputField<number, UpdateBotToolSettingsRequest>
                        required={false}
                        onChange={(v) => dispatch(updateSettings({pcpDealerContribution: v}))}
                        updateValidationStatus={updateValidationStatus}
                        fieldName={'pcpDealerContribution'}
                        selector={selectPcpDealerContribution}
                        validator={FloatNumberValidator}
                        disabled={isPcpIllustrationDisabled}
                        label={'PCP Dealer Contribution'}
                        placeholder={"e.g., 500"}
                        getMessage={getMessageWrapper}
                    />
                </div>
            )}

            {isIvendiSelected && (
                <>
                    <div className={styles.fieldsLineWithMargin}>
                        <ValidatedInputField<string, UpdateBotToolSettingsRequest>
                            required={false}
                            onChange={(v) => handleIvendiConfigChange('apiKey', v)}
                            updateValidationStatus={updateValidationStatus}
                            fieldName={'ivendiProviderConfig.apiKey'}
                            selector={selectIvendiApiKey}
                            validator={NotEmptyStringValidator}
                            disabled={isProviderDisabled}
                            label={'IVendi API Key'}
                            placeholder={"Enter API Key"}
                            getMessage={getMessageWrapper}
                        />
                        <ValidatedInputField<string, UpdateBotToolSettingsRequest>
                            required={false}
                            onChange={(v) => handleIvendiConfigChange('clientId', v)}
                            updateValidationStatus={updateValidationStatus}
                            fieldName={'ivendiProviderConfig.clientId'}
                            selector={selectIvendiClientId}
                            validator={NotEmptyStringValidator}
                            disabled={isProviderDisabled}
                            label={'IVendi Client ID'}
                            placeholder={"Enter Client ID"}
                            getMessage={getMessageWrapper}
                        />
                    </div>
                    <div className={styles.fieldsLineWithMargin}>
                        <ValidatedInputField<string, UpdateBotToolSettingsRequest>
                            required={false}
                            onChange={(v) => handleIvendiConfigChange('clientSecret', v)}
                            updateValidationStatus={updateValidationStatus}
                            fieldName={'ivendiProviderConfig.clientSecret'}
                            selector={selectIvendiClientSecret}
                            validator={NotEmptyStringValidator}
                            disabled={isProviderDisabled}
                            label={'IVendi Client Secret'}
                            placeholder={"Enter Client Secret"}
                            getMessage={getMessageWrapper}
                        />
                    </div>

                    <LocationQuoteeIdsTable
                        locationQuoteeIds={locationQuoteeIds}
                        disabled={isProviderDisabled}
                        updateValidationStatus={updateValidationStatus}
                        getMessage={getMessageWrapper}
                    />
                </>
            )}
        </Section>
    );
};
